import { Button, Group, Modal, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form"
import { useEffect } from "react";
import { OrganisationType } from "../../../../types/Organisation";
import { addUpdateOrganisation } from "../../../../services/UserService";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "../../../../hooks/useTranslation";
import useTransientStore from "../../../../data/transcientStore";

interface AddOrganisationFormProps {
    opened: boolean;
    open: () => void;
    close: () => void;
    onSuccess: () => void;
    allOrganisations: OrganisationType[];
}

interface EditOrganisationFormProps extends AddOrganisationFormProps {
    selectedOrg: OrganisationType
}


export function AddOrganisationForm({ opened, open, close, onSuccess, allOrganisations }: Readonly<AddOrganisationFormProps>) {
  const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
  const addrIdFromStore = selectedAddressSystem?.id || 0;

  const { getTranslation } = useTranslation({ pageId: 347, addressSystemId: addrIdFromStore || 0, autoLoad: true });
    const form = useForm({
        mode: 'controlled',
        initialValues: {
            id: null,
            name: '',
        },

        validate: {
            id: (value: null) => (value !== null ? (getTranslation("LABEL_ORG_FORM_ID_NULL") || "ID must be null.") : null),
            name: (value: string) => {
                if (!value || value.trim() === '') return getTranslation("LABEL_ORG_FORM_NAME_REQUIRED") || "Please enter an organisation name.";
                if (allOrganisations?.some(org => org.name?.toLowerCase().trim() === value.toLowerCase().trim())) {
                    return getTranslation("LABEL_ORG_FORM_NAME_TAKEN") || "This organisation name is already taken.";
                }
                return null;
            }
        }
    })


    return (
        <>
            {/* Change the on-submit once linked with the backend */}
            <Modal opened={opened} onClose={() => { form.reset(); close() }} title={getTranslation("TITLE_ADD_ORGANISATION") || "Add Organisation"} centered radius={'md'}>
                <form onSubmit={form.onSubmit(async (values) => {
                    await addUpdateOrganisation(values);
                    form.reset();
                    close();
                    onSuccess();
                    notifications.show({
                        title: getTranslation("LABEL_ORG_FORM_SUCCESS_TITLE") || "Success",
                        message: getTranslation("LABEL_ORG_FORM_SUBMITTED") || "Organisation Successfully Submitted.",
                        color: 'green',
                        withBorder: true,
                        withCloseButton: true,
                        autoClose: 5000,
                        className: 'notification-success'
                        // position: 'top-center'
                    })
                })}>

                    <TextInput
                        withAsterisk
                        label={getTranslation("LABEL_ORGANISATION_NAME") || "Organisation Name"}
                        placeholder={getTranslation("PLACEHOLDER_ENTER_ORGANISATION") || "Enter Organisation"}
                        key={form.key('name')}
                        {...form.getInputProps('name')}
                    />

                    <Group justify="flex-end" mt="md">
                        <Button type="submit">{getTranslation("LABEL_ORG_FORM_SUBMIT") || "Submit"}</Button>
                    </Group>
                </form>
            </Modal>
        </>
    )
}


export function EditOrganisationForm({ opened, open, close, onSuccess, selectedOrg, allOrganisations }: EditOrganisationFormProps) {
    const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
    const addrIdFromStore = selectedAddressSystem?.id || 0;

    const { getTranslation } = useTranslation({ pageId: 347, addressSystemId: addrIdFromStore || 0, autoLoad: true });

    useEffect(() => {
    }, [selectedOrg]);

    const form = useForm({
        mode: 'controlled',
        initialValues: {
            id: 0,
            name: '',
        },
        validate: {
            name: (value: string) => {
                if (!value || value.trim() === '') return getTranslation("LABEL_ORG_FORM_NAME_REQUIRED") || "Please enter an organisation name.";
                if (allOrganisations?.some(org => org.name?.toLowerCase().trim() === value.toLowerCase().trim() && org.id !== selectedOrg.id)) {
                    return getTranslation("LABEL_ORG_FORM_NAME_TAKEN") || "This organisation name is already taken.";
                }
                return null;
            }
        },
    });

    useEffect(() => {
        if (opened && selectedOrg) {
            form.setValues({
                id: selectedOrg.id || 0,
                name: selectedOrg.name || '',
            });
        }
    }, [opened, selectedOrg]);

    return (
        <Modal
            opened={opened}
            onClose={() => {
                form.reset();
                close();
            }}
            title={getTranslation("TITLE_EDIT_ORGANISATION") || "Edit Organisation"}
            centered
            radius={'md'}
        >
            <form
                onSubmit={form.onSubmit(async (values) => {
                    // TODO: Use this status to show approved/denied notifs
                    const status = await addUpdateOrganisation({ id: selectedOrg.id, name: values.name });
                    form.reset();
                    close();
                    onSuccess();
                    notifications.show({
                        title: getTranslation("LABEL_ORG_FORM_SUCCESS_TITLE") || "Success",
                        message: getTranslation("LABEL_ORG_FORM_UPDATED") || "Organisation Successfully Updated.",
                        color: 'green',
                        withBorder: true,
                        withCloseButton: true,
                        autoClose: 5000,
                        className: 'notification-success'
                        // position: 'top-center',
                    });
                })}
            >
                <TextInput
                    withAsterisk
                    label={getTranslation("LABEL_ORGANISATION_NAME") || "Organisation Name"}
                    placeholder={selectedOrg.name}
                    key={form.key('name')}
                    {...form.getInputProps('name')}
                />

                <Group justify="flex-end" mt="md">
                    <Button type="submit">{getTranslation("LABEL_ORG_FORM_SUBMIT") || "Submit"}</Button>
                </Group>
            </form>
        </Modal>
    );
}